/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC, PropsWithRef } from "react";
import { useEffect } from "react";
import { Dropdown } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IUser } from "../../types";
import { signOut } from "firebase/auth";
import { firebaseAuth } from "../../firebaseConfig";
import { useUserSession } from "../../context/UserContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useUserWorkspaces } from "../../hooks";
import { IWorkspaceAccess } from "../../types";
import { UserAvatar } from "@components";
import { HiChevronDown } from "react-icons/hi";

type UserDropdownProps = PropsWithRef<IUser> & {
  variant?: "avatar" | "workspace";
};

export const UserDropdown: FC<UserDropdownProps> = function (
  user: UserDropdownProps,
) {
  const navigate = useNavigate();
  const { t } = useTranslation("navigation");
  const { userSession, setUserSession, revokeUserSession } = useUserSession();
  const { activeWorkspace, setActiveWorkspace } = useWorkspace();
  const query = useUserWorkspaces(userSession?.user.email ?? "");

  // Await Firebase sign-out and clear all local state (session + active
  // workspace) before navigating, so logout never races an eager document
  // reload that leaves the previous tenant context behind.
  const handleLogout = async () => {
    try {
      await signOut(firebaseAuth);
    } finally {
      setUserSession(undefined);
      setActiveWorkspace(undefined);
      revokeUserSession();
      navigate("/login");
    }
  };

  let workspaces: IWorkspaceAccess[] = [];
  if (query && !query.isLoading && query.isFetched && query.data)
    workspaces = query.data;

  useEffect(() => {}, [activeWorkspace, setActiveWorkspace]);

  if (userSession == null || query.isLoading) return null;

  return (
    <Dropdown
      arrowIcon={false}
      inline
      label={
        <span
          className={
            user.variant === "workspace"
              ? "flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text shadow-sm transition-colors hover:bg-surface-hover"
              : undefined
          }
        >
          <span className="sr-only">{t("navbar.title")}</span>
          {user.variant === "workspace" ? (
            <span className="max-w-48 truncate">
              {activeWorkspace?.workspaceName ?? t("navbar.my_workspaces")}
            </span>
          ) : null}
          <UserAvatar className="h-8 rounded" />
          {user.variant === "workspace" ? (
            <HiChevronDown className="h-4 w-4 text-muted" />
          ) : null}
        </span>
      }
    >
      {user.variant !== "workspace" ? (
        <>
          <Dropdown.Header>
            {user.displayName && (
              <span className="block text-sm">{user.displayName}</span>
            )}
            <span className="block truncate text-sm font-medium">
              {user.email}
            </span>
          </Dropdown.Header>
          <Dropdown.Item onClick={() => navigate("/settings?tab=profile")}>
            {t("navbar.profile")}
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item
            className="font-light"
            onClick={() => navigate("/settings?tab=my-workspaces")}
          >
            {t("navbar.my_workspaces")}
          </Dropdown.Item>
        </>
      ) : null}
      {workspaces.map((workspace) => (
        <Dropdown.Item
          className={user.variant === "workspace" ? "" : "ml-2"}
          key={workspace.workspaceId}
          onClick={() => {
            setActiveWorkspace(workspace);
            navigate("/");
          }}
        >
          {workspace.workspaceName}
        </Dropdown.Item>
      ))}
      <Dropdown.Divider />

      <Dropdown.Item onClick={handleLogout}>{t("navbar.logout")}</Dropdown.Item>
    </Dropdown>
  );
};
